import axios from 'axios';
import type { View } from '../../routes';
import { MENU_ROLES } from '../../routes';
import { AUTH_URL } from '../../lib/service-registry';

/**
 * MYB-364: Fetch menu permissions from admin.tscorp.ai (Auth service).
 * Cached in sessionStorage for 60 seconds. Falls back to hardcoded MENU_ROLES on error.
 */

const AUTH_MENU_PERMISSIONS_URL = `${AUTH_URL}/api/platform/menu-permissions/hr`;
const CACHE_KEY = 'tscorp_menu_permissions';
const CACHE_TTL_MS = 60 * 1000; // 60 seconds

/** Permission level for a menu item per role */
export type MenuPermission = 'RW' | 'R' | null;

/** API response shape */
export interface MenuPermissionsResponse {
  menus: Record<string, Record<string, string>>;
}

/** Parsed permission map: menuId -> role -> permission */
export type MenuPermissionMap = Record<string, Record<string, MenuPermission>>;

interface CachedPermissions {
  data: MenuPermissionMap;
  fetchedAt: number;
}

/**
 * Build default permission map from hardcoded MENU_ROLES.
 * All allowed roles get 'RW', everything else is null.
 */
function buildDefaultPermissions(): MenuPermissionMap {
  const result: MenuPermissionMap = {};
  for (const [menuId, roles] of Object.entries(MENU_ROLES)) {
    result[menuId] = {};
    for (const role of roles) {
      result[menuId][role] = 'RW';
    }
  }
  return result;
}

const defaultPermissions = buildDefaultPermissions();

/**
 * Read cached permissions from sessionStorage if still fresh.
 */
function readCache(): MenuPermissionMap | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedPermissions = JSON.parse(raw);
    if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached.data;
  } catch {
    sessionStorage.removeItem(CACHE_KEY);
    return null;
  }
}

/**
 * Write permissions to sessionStorage cache.
 */
function writeCache(data: MenuPermissionMap): void {
  try {
    const cached: CachedPermissions = { data, fetchedAt: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

/**
 * Normalize API response into typed MenuPermissionMap.
 */
function normalizeResponse(resp: MenuPermissionsResponse): MenuPermissionMap {
  const result: MenuPermissionMap = {};
  for (const [menuId, rolePerms] of Object.entries(resp.menus)) {
    result[menuId] = {};
    for (const [role, perm] of Object.entries(rolePerms)) {
      const upper = (perm || '').toUpperCase();
      result[menuId][role] = upper === 'RW' ? 'RW' : upper === 'R' ? 'R' : null;
    }
  }
  return result;
}

/**
 * Fetch menu permissions from Auth service.
 * Returns cached result if fresh, otherwise fetches and caches.
 * Falls back to hardcoded defaults on any error.
 */
export async function fetchMenuPermissions(): Promise<MenuPermissionMap> {
  // Check cache first
  const cached = readCache();
  if (cached) return cached;

  try {
    // MYB-373 Phase 4: Menu permissions endpoint is public (no auth required).
    // No need to send credentials — data is role->menu mapping, not user-specific.
    const resp = await axios.get<MenuPermissionsResponse>(AUTH_MENU_PERMISSIONS_URL, {
      timeout: 5000,
    });
    const permissions = normalizeResponse(resp.data);
    writeCache(permissions);
    return permissions;
  } catch {
    // API unreachable or error — fall back to hardcoded defaults
    return defaultPermissions;
  }
}

/**
 * Resolve the effective permission for a menu item given the user's role.
 * Checks fetched permissions first, then falls back to hardcoded MENU_ROLES.
 */
export function getMenuPermission(
  permissions: MenuPermissionMap,
  menuId: View,
  role: string
): MenuPermission {
  // If the API returned permissions for this menu, use them
  if (permissions[menuId] && role in permissions[menuId]) {
    return permissions[menuId][role];
  }
  // Fallback: check hardcoded MENU_ROLES
  const hardcodedRoles = MENU_ROLES[menuId];
  if (hardcodedRoles && hardcodedRoles.includes(role)) {
    return 'RW';
  }
  return null;
}
